import { useEffect, useMemo, useRef, useState } from 'react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { toast } from 'sonner';

// Single source of truth for the option lists. Kept in sync with
// ProjectsPage.js — exported from here so any future consumer (e.g.
// ClientDetailPage) gets the same labels without duplicating.
export const PROJECT_STATUS_OPTIONS = [
  { value: 'planning', label: 'Planning' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'on_hold', label: 'On Hold' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

export const PROJECT_PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

export const PROJECT_TYPE_OPTIONS = [
  { value: 'web_development', label: 'Web Development' },
  { value: 'mobile_app', label: 'Mobile App' },
  { value: 'ui_ux', label: 'UI/UX Design' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'consulting', label: 'Consulting' },
  { value: 'other', label: 'Other' },
];

const emptyForm = {
  name: '', client_id: '', description: '', project_type: 'web_development',
  status: 'planning', priority: 'medium', budget: '', start_date: '', end_date: '', members: [],
};

/**
 * Reusable Add/Edit Project dialog.
 *
 * Props:
 *  - open / onOpenChange:  controlled visibility (parent owns the trigger).
 *  - project:              when set → edit mode, otherwise create mode.
 *  - defaultClientId:      prefill the client in create mode (e.g. when
 *                          launched from a client detail page).
 *  - lockClient:           hide the client picker entirely. Used when the
 *                          caller already knows the client and doesn't
 *                          want the user to change it.
 *  - clients / allUsers:   optional caller-provided lookups. If omitted
 *                          we fetch them lazily on first open so the
 *                          dialog stays self-sufficient.
 *  - onSaved:              fired after a successful create/update.
 */
export default function ProjectFormDialog({
  open,
  onOpenChange,
  project = null,
  defaultClientId = '',
  lockClient = false,
  clients: clientsProp,
  allUsers: allUsersProp,
  onSaved,
}) {
  const isEditing = !!project;
  const [formData, setFormData] = useState(emptyForm);
  const [clients, setClients] = useState(clientsProp || []);
  const [allUsers, setAllUsers] = useState(allUsersProp || []);
  const [submitting, setSubmitting] = useState(false);

  // Autocomplete state for the client picker.
  const [clientSearch, setClientSearch] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const clientPickerRef = useRef(null);

  // Lazy-load lookups the first time the dialog opens, but only if the
  // caller didn't pre-provide them.
  useEffect(() => {
    if (!open) return;
    if (!clientsProp || clientsProp.length === 0) {
      api.get('/clients?limit=500&sort_by=created_at&sort_order=desc')
        .then(r => { if (r.data.success) setClients(r.data.data); })
        .catch(() => {});
    }
    if (!allUsersProp || allUsersProp.length === 0) {
      api.get('/users?status=active')
        .then(r => { if (r.data.success) setAllUsers(r.data.data); })
        .catch(() => {});
    }
  }, [open, clientsProp, allUsersProp]);

  // Keep local lists in sync when the caller passes fresh data.
  useEffect(() => { if (clientsProp) setClients(clientsProp); }, [clientsProp]);
  useEffect(() => { if (allUsersProp) setAllUsers(allUsersProp); }, [allUsersProp]);

  // Seed form whenever the dialog opens.
  useEffect(() => {
    if (!open) return;
    if (project) {
      setFormData({
        name: project.name || '',
        client_id: project.client_id || '',
        description: project.description || '',
        project_type: project.project_type || 'web_development',
        status: project.status || 'planning',
        priority: project.priority || 'medium',
        budget: project.budget?.toString() || '',
        start_date: project.start_date || '',
        end_date: project.end_date || '',
        members: project.members || [],
      });
      setClientSearch(project.client_name || '');
    } else {
      setFormData({ ...emptyForm, client_id: defaultClientId || '' });
      setClientSearch('');
    }
    setShowClientDropdown(false);
  }, [open, project, defaultClientId]);

  // Close client autocomplete on outside click.
  useEffect(() => {
    const handler = (e) => {
      if (clientPickerRef.current && !clientPickerRef.current.contains(e.target)) {
        setShowClientDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const clientSuggestions = useMemo(() => {
    if (!clientSearch.trim()) return clients.slice(0, 5);
    const q = clientSearch.trim().toLowerCase();
    return clients.filter(c => (c.company_name || '').toLowerCase().includes(q)).slice(0, 5);
  }, [clientSearch, clients]);

  const selectedClient = clients.find(c => c.id === formData.client_id);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name?.trim()) return toast.error('Project name is required');
    if (!formData.client_id) return toast.error('Please select a client');
    setSubmitting(true);
    try {
      let saved;
      if (isEditing) {
        const res = await api.put(`/projects/${project.id}`, formData);
        saved = res.data?.data || { ...project, ...formData };
        toast.success('Project updated');
      } else {
        const res = await api.post('/projects', formData);
        saved = res.data?.data;
        toast.success('Project created');
      }
      onOpenChange?.(false);
      onSaved?.(saved);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save project');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="project-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Project' : 'Create New Project'}</DialogTitle>
          <DialogDescription>
            {isEditing ? 'Update project details' : 'Enter project information'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="project-name-input">Project Name *</Label>
              <Input
                id="project-name-input"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter project name"
                required
                data-testid="project-name"
              />
            </div>

            {!lockClient && (
              <div className="space-y-2" ref={clientPickerRef}>
                <Label htmlFor="project-client-input">Client *</Label>
                <div className="relative">
                  <Input
                    id="project-client-input"
                    value={showClientDropdown ? clientSearch : (selectedClient?.company_name || clientSearch)}
                    onChange={(e) => {
                      setClientSearch(e.target.value);
                      setShowClientDropdown(true);
                      if (formData.client_id) {
                        setFormData(f => ({ ...f, client_id: '' }));
                      }
                    }}
                    onFocus={() => {
                      setShowClientDropdown(true);
                      if (!clientSearch && selectedClient) {
                        setClientSearch(selectedClient.company_name || '');
                      }
                    }}
                    placeholder="Type to search clients…"
                    data-testid="project-client-input"
                    autoComplete="off"
                  />
                  {showClientDropdown && clientSuggestions.length > 0 && (
                    <div
                      className="absolute z-50 mt-1 w-full rounded-md border border-border bg-popover shadow-lg max-h-60 overflow-auto"
                      data-testid="project-client-dropdown"
                    >
                      {clientSuggestions.map(client => (
                        <button
                          key={client.id}
                          type="button"
                          className="w-full text-left px-3 py-2 hover:bg-accent text-sm"
                          data-testid={`project-client-option-${client.id}`}
                          onClick={() => {
                            setFormData({ ...formData, client_id: client.id });
                            setClientSearch(client.company_name || '');
                            setShowClientDropdown(false);
                          }}
                        >
                          {client.company_name}
                          {client.contact_person && (
                            <span className="text-xs text-muted-foreground ml-2">
                              · {client.contact_person}
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                  {showClientDropdown && clientSuggestions.length === 0 && (
                    <div className="absolute z-50 mt-1 w-full rounded-md border border-border bg-popover shadow-lg p-3 text-sm text-muted-foreground">
                      No clients match &quot;{clientSearch}&quot;.
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label>Project Type</Label>
              <Select
                value={formData.project_type}
                onValueChange={(value) => setFormData({ ...formData, project_type: value })}
              >
                <SelectTrigger data-testid="project-type">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {PROJECT_TYPE_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select
                value={formData.status}
                onValueChange={(value) => setFormData({ ...formData, status: value })}
              >
                <SelectTrigger data-testid="project-status">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {PROJECT_STATUS_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Priority</Label>
              <Select
                value={formData.priority}
                onValueChange={(value) => setFormData({ ...formData, priority: value })}
              >
                <SelectTrigger data-testid="project-priority">
                  <SelectValue placeholder="Select priority" />
                </SelectTrigger>
                <SelectContent>
                  {PROJECT_PRIORITY_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="project-budget-input">Budget (INR)</Label>
              <Input
                id="project-budget-input"
                type="number"
                value={formData.budget}
                onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                placeholder="500000"
                data-testid="project-budget"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="project-start-date-input">Start Date</Label>
              <Input
                id="project-start-date-input"
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                data-testid="project-start-date"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="project-end-date-input">End Date</Label>
              <Input
                id="project-end-date-input"
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                data-testid="project-end-date"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="project-description-input">Description</Label>
            <Textarea
              id="project-description-input"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Project description..."
              rows={3}
            />
          </div>
          <div className="space-y-2">
            <Label>Assign Team Members</Label>
            <div className="max-h-[150px] overflow-y-auto rounded-md border border-border p-2 space-y-1">
              {allUsers.map(u => (
                <label key={u.id} className="flex items-center gap-2 p-1.5 rounded hover:bg-muted/20 cursor-pointer text-sm">
                  <input
                    type="checkbox"
                    className="rounded border-border"
                    checked={(formData.members || []).includes(u.id)}
                    onChange={e => {
                      const members = formData.members || [];
                      setFormData({
                        ...formData,
                        members: e.target.checked ? [...members, u.id] : members.filter(x => x !== u.id),
                      });
                    }}
                  />
                  <span>{u.first_name} {u.last_name}</span>
                  <span className="text-xs text-muted-foreground ml-auto">{u.email}</span>
                </label>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">
              {(formData.members || []).length} member{(formData.members || []).length !== 1 ? 's' : ''} selected
            </p>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange?.(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting} data-testid="project-submit-btn">
              {submitting ? 'Saving…' : (isEditing ? 'Update Project' : 'Create Project')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
